import axios from 'axios';
import { API_BASE_URL } from '../utils/config';
import {
  CreateTableReservationRequest,
  TableAvailability,
  PendingReservation,
  ApproveReservationRequest,
} from '../types/calendar';

export interface EventDto {
  eventId: string;
  eventName: string;
  eventDate: string;
  startTime: string;
  endTime: string;
  eventTypeName: string;
  eventTypeDisplayName: string;
  eventTypeColor: string;
  venueName: string;
  createdByName: string;
  /**
   * [magánóra-sorozat, 2026-10-31] A létrehozó azonosítója — a `GET /api/events` lista adja
   * (`backend/PinyoApi/Controllers/EventController.cs` lista-projekció, `CreatedBy = e.CreatedBy`). Ebből dől el a
   * felületen a „saját sorozat” (`utils/eventSeries.ts` `isSeriesOwnedBy`); a szerver a mérvadó.
   */
  createdBy?: string;
  recurringEventGroupId?: string | null;
  description?: string;
  maxParticipants?: number;
  requiredTables: number;
  participantCount: number;
  isActive: boolean;
  isPaidOut?: boolean;
}

export interface CreateEventDto {
  eventName: string;
  eventDate: string;
  startTime: string;
  endTime: string;
  eventTypeId: string;
  venueId: string;
  description?: string;
  maxParticipants?: number;
  requiredTables: number;
  isActive: boolean;
}

export interface EventTypeDto {
  eventTypeId: string;
  name: string;
  displayName: string;
  color: string;
  description?: string;
  defaultVenueId?: string;
  isActive: boolean;
}

export interface TimeSlotDto {
  dayOfWeek?: number; // 0=Sunday, 1=Monday, ..., 6=Saturday
  startTime: string;
  endTime: string;
}

export interface VenueDto {
  venueId: string;
  name: string;
  address: string;
  city: string;
  postalCode: string;
  country: string;
  description?: string;
  capacity?: number;
  tableCount: number;
  isActive: boolean;
  // Table Reservation Settings
  tableReservationEnabled: boolean;
  tableReservationTimeSlots?: TimeSlotDto[];
  allowTableReservationDuringGroupTraining: boolean;
}

class EventService {
  private getAuthHeader() {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  async getEvents(params?: {
    startDate?: string;
    endDate?: string;
    eventTypeId?: string;
    venueId?: string;
    userId?: string;
    view?: string;
    /** Egy ismétlődő sorozat összes előfordulása (a sorozat bővítéséhez). */
    recurringEventGroupId?: string;
  }): Promise<EventDto[]> {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/events`, {
        headers: this.getAuthHeader(),
        params
      });
      const result = response.data;
      return Array.isArray(result) ? result : result.data || [];
    } catch (error) {
      console.error('Error fetching events:', error);
      throw error;
    }
  }

  async getEvent(id: string): Promise<EventDto> {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/events/${id}`, {
        headers: this.getAuthHeader()
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching event:', error);
      throw error;
    }
  }

  async createEvent(event: CreateEventDto): Promise<EventDto> {
    try {
      const response = await axios.post(`${API_BASE_URL}/api/events`, event, {
        headers: this.getAuthHeader()
      });
      return response.data;
    } catch (error) {
      console.error('Error creating event:', error);
      throw error;
    }
  }

  async updateEvent(id: string, event: Partial<CreateEventDto>): Promise<EventDto> {
    try {
      const response = await axios.put(`${API_BASE_URL}/api/events/${id}`, event, {
        headers: this.getAuthHeader()
      });
      return response.data;
    } catch (error) {
      console.error('Error updating event:', error);
      throw error;
    }
  }

  async deleteEvent(id: string): Promise<void> {
    try {
      await axios.delete(`${API_BASE_URL}/api/events/${id}`, {
        headers: this.getAuthHeader()
      });
    } catch (error) {
      console.error('Error deleting event:', error);
      throw error;
    }
  }

  async getEventTypes(): Promise<EventTypeDto[]> {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/event-types`, {
        headers: this.getAuthHeader()
      });
      // Backend returns { success: true, data: [...] }
      return response.data.data || response.data;
    } catch (error) {
      console.error('Error fetching event types:', error);
      throw error;
    }
  }

  async getVenues(): Promise<VenueDto[]> {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/venues/active`, {
        headers: this.getAuthHeader()
      });
      // A backend BURKOLVA ad vissza: `{ success: true, data: [...] }`
      // (VenuesController.GetActiveVenues). A korábbi komment azt állította, hogy csupasz
      // tömb jön, és a metódus a burkolót adta tovább — ettől az Edző Elérhetőség
      // szerkesztés-dialógusa TypeError-t dobott (`.filter` egy objektumon), a napi/heti/
      // naptár nézetek helyszín-szűrője pedig csendben üresen maradt. A projektben bevett
      // minta a `response.data.data || response.data` (lásd venueService, getEventTypes).
      return response.data.data || response.data;
    } catch (error) {
      console.error('Error fetching venues:', error);
      throw error;
    }
  }

  // Helper method to get events for a specific month
  async getMonthEvents(year: number, month: number): Promise<EventDto[]> {
    // Calculate the full calendar view range (6 weeks = 42 days)
    const firstDayOfMonth = new Date(year, month - 1, 1);
    const firstDayWeekday = (firstDayOfMonth.getDay() || 7) - 1; // Monday = 0

    // Start from the first day shown in calendar (may be from previous month)
    const calendarStartDate = new Date(year, month - 1, 1 - firstDayWeekday);

    // End at the last day shown in calendar (42 days total for 6 weeks)
    const calendarEndDate = new Date(calendarStartDate);
    calendarEndDate.setDate(calendarStartDate.getDate() + 41); // 42 days total (0-41 = 42 days)

    const startDate = calendarStartDate.toISOString().split('T')[0];
    const endDate = calendarEndDate.toISOString().split('T')[0];

    return this.getEvents({
      startDate,
      endDate,
      view: 'monthly'
    });
  }

  // Helper method to get events for a specific day
  async getDayEvents(date: string): Promise<EventDto[]> {
    return this.getEvents({
      startDate: date,
      endDate: date,
      view: 'daily'
    });
  }

  // Table Reservation Methods
  async createTableReservation(request: CreateTableReservationRequest): Promise<EventDto> {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/events/table-reservation`,
        request,
        {
          headers: this.getAuthHeader()
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error creating table reservation:', error);
      throw error;
    }
  }

  // Private lesson request (magánóra igénylése). Every field is optional; the backend emails the
  // administrators and confirms to the requester. Nothing is booked as an event.
  async requestPrivateLesson(payload: {
    preferredCoachIds?: string[];
    preferredDate?: string | null; // 'YYYY-MM-DD'
    preferredTime?: string | null; // 'HH:mm'
    note?: string;
  }): Promise<void> {
    try {
      await axios.post(
        `${API_BASE_URL}/api/events/request-private-lesson`,
        payload,
        {
          headers: this.getAuthHeader()
        }
      );
    } catch (error) {
      console.error('Error requesting private lesson:', error);
      throw error;
    }
  }

  async checkTableAvailability(
    venueId: string,
    date: string,
    startTime: string,
    endTime: string,
    requiredTables: number
  ): Promise<TableAvailability> {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/events/table-availability`,
        {
          headers: this.getAuthHeader(),
          params: {
            venueId,
            date,
            startTime,
            endTime,
            requiredTables
          }
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error checking table availability:', error);
      throw error;
    }
  }

  async getPendingReservations(): Promise<PendingReservation[]> {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/events/pending-reservations`,
        {
          headers: this.getAuthHeader()
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching pending reservations:', error);
      throw error;
    }
  }

  async approveReservation(
    eventId: string,
    request: ApproveReservationRequest
  ): Promise<EventDto> {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/events/${eventId}/approve-reservation`,
        request,
        {
          headers: this.getAuthHeader()
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error approving/rejecting reservation:', error);
      throw error;
    }
  }
}

export default new EventService();